import React, { useState, useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist';
import MintDBLoader from './MintDBLoader.tsx';

interface MatrixData {
  drug_name: string;
  gene_name: string;
  disorder_name: string;
}

const DrugGeneMatrix: React.FC = () => {
  const [data, setData] = useState<MatrixData[]>([]);
  const [mutationData, setMutationData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const plotRef = useRef<HTMLDivElement>(null);

  // Filter states
  const [selectedDisorders, setSelectedDisorders] = useState<string[]>([]);
  const [selectedGenes, setSelectedGenes] = useState<string[]>([]);
  const [selectedDrugs, setSelectedDrugs] = useState<string[]>([]);
  const [allDisorders, setAllDisorders] = useState<string[]>([]);
  const [allGenes, setAllGenes] = useState<string[]>([]);
  const [allDrugs, setAllDrugs] = useState<string[]>([]);
  const [showDisorderDropdown, setShowDisorderDropdown] = useState(false);
  const [showGeneDropdown, setShowGeneDropdown] = useState(false);
  const [showDrugDropdown, setShowDrugDropdown] = useState(false);
  const [disorderSearchTerm, setDisorderSearchTerm] = useState('');
  const [geneSearchTerm, setGeneSearchTerm] = useState('');
  const [drugSearchTerm, setDrugSearchTerm] = useState('');

  const disorderDropdownRef = useRef<HTMLDivElement>(null);
  const geneDropdownRef = useRef<HTMLDivElement>(null);
  const drugDropdownRef = useRef<HTMLDivElement>(null);

  // Disorder color mapping
  const disorderColors: { [key: string]: string } = {
    'adhd': '#EF4444',           // red
    'anxiety': '#F97316',        // orange
    'bipolar': '#9333EA',        // purple
    'depression': '#3B82F6',     // blue
    'schizophrenia': '#10B981',  // green
    'ocd': '#EC4899',            // pink
    'ptsd': '#F59E0B',           // amber
    'autism': '#8B5CF6',         // violet
    'insomnia': '#14B8A6',       // teal
    'panic': '#F43F5E'           // rose
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      // Fetch both datasets in parallel
      const [matrixResponse, mutationResponse] = await Promise.all([
        fetch('/api/drug-gene-matrix'),
        fetch('/api/mutation-counts')
      ]);

      if (!matrixResponse.ok) {
        throw new Error('Failed to fetch drug-gene matrix data');
      }

      const matrixResult = await matrixResponse.json();
      setData(matrixResult);

      if (mutationResponse.ok) {
        const mutationResult = await mutationResponse.json();
        setMutationData(mutationResult);
      }

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data.length > 0) {
      const disorders = Array.from(new Set(data.map(d => d.disorder_name))).sort();
      const genes = Array.from(new Set(data.map(d => d.gene_name))).sort();
      const drugs = Array.from(new Set(data.map(d => d.drug_name))).sort();

      setAllDisorders(disorders);
      setAllGenes(genes);
      setAllDrugs(drugs);

      setSelectedDisorders(disorders);
      setSelectedGenes(genes);
      setSelectedDrugs(drugs);
    }
  }, [data]);

  // Cascading filter: compute available drugs and genes based on current selections
  const availableDrugs = Array.from(new Set(
    data
      .filter(d => selectedDisorders.some(sd => sd.toLowerCase() === d.disorder_name.toLowerCase()))
      .map(d => d.drug_name)
  )).sort();

  const availableGenes = Array.from(new Set(
    data
      .filter(d =>
        selectedDisorders.some(sd => sd.toLowerCase() === d.disorder_name.toLowerCase()) &&
        selectedDrugs.some(sd => sd.toLowerCase() === d.drug_name.toLowerCase())
      )
      .map(d => d.gene_name)
  )).sort();

  // When disorders change, prune drugs and genes to only available ones
  useEffect(() => {
    setSelectedDrugs(prev => prev.filter(d => availableDrugs.includes(d)));
  }, [selectedDisorders]);

  useEffect(() => {
    setSelectedGenes(prev => prev.filter(g => availableGenes.includes(g)));
  }, [selectedDrugs, selectedDisorders]);

  useEffect(() => {
    if (data.length > 0 && plotRef.current && selectedDisorders.length > 0 && selectedGenes.length > 0 && selectedDrugs.length > 0) {
      renderPlot();
    }
  }, [data, selectedDisorders, selectedGenes, selectedDrugs]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (disorderDropdownRef.current && !disorderDropdownRef.current.contains(event.target as Node)) {
        setShowDisorderDropdown(false);
      }
      if (geneDropdownRef.current && !geneDropdownRef.current.contains(event.target as Node)) {
        setShowGeneDropdown(false);
      }
      if (drugDropdownRef.current && !drugDropdownRef.current.contains(event.target as Node)) {
        setShowDrugDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDisorderToggle = (disorder: string) => {
    setSelectedDisorders(prev =>
      prev.includes(disorder)
        ? prev.filter(d => d !== disorder)
        : [...prev, disorder]
    );
  };

  const handleGeneToggle = (gene: string) => {
    setSelectedGenes(prev =>
      prev.includes(gene)
        ? prev.filter(g => g !== gene)
        : [...prev, gene]
    );
  };

  const handleSelectAllDisorders = () => {
    setSelectedDisorders(allDisorders);
  };

  const handleDeselectAllDisorders = () => {
    setSelectedDisorders([]);
  };

  const handleSelectAllGenes = () => {
    setSelectedGenes(availableGenes);
  };

  const handleDeselectAllGenes = () => {
    setSelectedGenes([]);
  };

  const handleDrugToggle = (drug: string) => {
    setSelectedDrugs(prev =>
      prev.includes(drug)
        ? prev.filter(d => d !== drug)
        : [...prev, drug]
    );
  };

  const handleSelectAllDrugs = () => {
    setSelectedDrugs(availableDrugs);
  };

  const handleDeselectAllDrugs = () => {
    setSelectedDrugs([]);
  };

  const renderPlot = () => {
    if (!plotRef.current || data.length === 0) return;
    if (selectedDisorders.length === 0 || selectedGenes.length === 0 || selectedDrugs.length === 0) return;

    // Purge any existing plot to avoid state issues
    Plotly.purge(plotRef.current);

    // Filter drug-gene data by all three: disorders, genes, and drugs
    const filteredDrugGeneData = data.filter(item =>
      selectedDisorders.some(d => d.toLowerCase() === item.disorder_name.toLowerCase()) &&
      selectedGenes.some(g => g.toLowerCase() === item.gene_name.toLowerCase()) &&
      selectedDrugs.some(dr => dr.toLowerCase() === item.drug_name.toLowerCase())
    );

    // Filter mutation data by disorders and genes
    const filteredMutationData = mutationData.filter(item =>
      selectedDisorders.includes(item.disorder_name) &&
      selectedGenes.includes(item.gene_name)
    );

    if (filteredDrugGeneData.length === 0) {
      // Clear the plot if no data matches
      Plotly.purge(plotRef.current);
      return;
    }

    // Get unique drugs from filtered data, and only the selected genes (sorted)
    const uniqueDrugs = Array.from(new Set(filteredDrugGeneData.map(d => d.drug_name))).sort();
    const uniqueGenes = [...selectedGenes].sort();
    const uniqueDisorders = [...selectedDisorders].sort();

    // Group by disorder for drug-gene scatter plot
    const disorderGroups: { [key: string]: MatrixData[] } = {};
    filteredDrugGeneData.forEach(item => {
      const disorder = item.disorder_name.toLowerCase();
      if (!disorderGroups[disorder]) {
        disorderGroups[disorder] = [];
      }
      disorderGroups[disorder].push(item);
    });

    // Create traces for each disorder
    const traces = Object.entries(disorderGroups).map(([disorder, items]) => {
      const x: string[] = [];
      const y: string[] = [];
      const text: string[] = [];
      const hoverText: string[] = [];

      items.forEach(item => {
        x.push(item.drug_name);
        y.push(item.gene_name);
        text.push(disorder);
        hoverText.push(
          `<b>Drug:</b> ${item.drug_name}<br>` +
          `<b>Gene:</b> ${item.gene_name}<br>` +
          `<b>Disorder:</b> ${item.disorder_name}`
        );
      });

      return {
        x: x,
        y: y,
        mode: 'markers',
        type: 'scatter',
        name: disorder.charAt(0).toUpperCase() + disorder.slice(1),
        marker: {
          size: 7,
          color: disorderColors[disorder] || '#6B7280',
          symbol: 'circle',
          opacity: 0.85,
          line: {
            color: 'rgba(0,0,0,0.3)',
            width: 0.5
          }
        },
        text: hoverText,
        hovertemplate: '%{text}<extra></extra>',
        showlegend: true
      };
    });

    // Create heatmap matrix (use pre-filtered mutation data)
    const matrix: number[][] = [];
    const annotations: any[] = [];

    uniqueGenes.forEach((gene) => {
      const row: number[] = [];
      uniqueDisorders.forEach((disorder) => {
        const dataPoint = filteredMutationData.find(
          d => d.gene_name === gene && d.disorder_name === disorder
        );
        const count = dataPoint ? parseInt(dataPoint.mutation_count.toString()) : 0;
        row.push(count);

        // Add annotation for the cell value
        annotations.push({
          x: disorder,
          y: gene,
          text: count > 0 ? count.toString() : '',
          font: {
            size: 8,
            color: count > 15 ? 'white' : 'black',
            family: 'Arial, sans-serif'
          },
          showarrow: false,
          xref: 'x2',
          yref: 'y2'
        });
      });
      matrix.push(row);
    });

    // Add heatmap trace
    if (matrix.length > 0) {
      const heatmapTrace = {
        z: matrix,
        x: uniqueDisorders,
        y: uniqueGenes,
        type: 'heatmap',
        colorscale: [
          [0, 'white'],
          [0.2, '#D1D9E6'],
          [0.4, '#9EADC7'],
          [0.6, '#6B84AA'],
          [0.8, '#3D5A8C'],
          [1, '#1A2940']
        ],
        showscale: uniqueGenes.length > 1,
        colorbar: {
          title: {
            text: 'Mutations',
            side: 'right',
            font: {
              size: 10,
              color: '#1A2940',
              family: 'Arial, sans-serif'
            }
          },
          thickness: 12,
          len: 0.5,
          x: 1.01,
          tickfont: {
            size: 8,
            color: '#4B5563',
            family: 'Arial, sans-serif'
          },
          outlinewidth: 0,
          borderwidth: 0
        },
        hoverongaps: false,
        hovertemplate: '<b>Disorder:</b> %{x}<br><b>Gene:</b> %{y}<br><b>Mutations:</b> %{z}<extra></extra>',
        xaxis: 'x2',
        yaxis: 'y2',
        showlegend: false
      };

      traces.push(heatmapTrace);
    }

    const layout = {
      xaxis: {
        title: {
          text: 'Drugs',
          font: {
            size: 11,
            color: '#1A2940',
            family: 'Arial, sans-serif'
          }
        },
        type: 'category',
        categoryarray: uniqueDrugs,
        tickangle: -45,
        tickfont: {
          size: 8,
          color: '#4B5563',
          family: 'Arial, sans-serif'
        },
        showgrid: false,
        zeroline: false,
        domain: [0, 0.48],
        showline: false,
        fixedrange: true
      },
      xaxis2: {
        title: {
          text: 'Disorders',
          font: {
            size: 11,
            color: '#1A2940',
            family: 'Arial, sans-serif'
          }
        },
        type: 'category',
        categoryarray: uniqueDisorders,
        tickangle: -45,
        tickfont: {
          size: 8,
          color: '#4B5563',
          family: 'Arial, sans-serif'
        },
        showgrid: false,
        zeroline: false,
        domain: [0.54, 1],
        anchor: 'y2',
        showline: false,
        fixedrange: true
      },
      yaxis: {
        title: {
          text: 'Gene Symbols',
          font: {
            size: 11,
            color: '#1A2940',
            family: 'Arial, sans-serif'
          }
        },
        type: 'category',
        categoryarray: uniqueGenes,
        tickfont: {
          size: 8,
          color: '#4B5563',
          family: 'Arial, sans-serif'
        },
        showgrid: false,
        zeroline: false,
        fixedrange: true,
        showline: false
      },
      yaxis2: {
        title: {
          text: 'Gene Symbols',
          font: {
            size: 11,
            color: '#1A2940',
            family: 'Arial, sans-serif'
          }
        },
        type: 'category',
        categoryarray: uniqueGenes,
        tickfont: {
          size: 8,
          color: '#4B5563',
          family: 'Arial, sans-serif'
        },
        showgrid: false,
        zeroline: false,
        fixedrange: true,
        showline: false,
        anchor: 'x2',
        side: 'left'
      },
      annotations: [
        // Title for drug-gene scatter plot
        {
          text: 'Drug-Gene Relationships',
          xref: 'paper',
          yref: 'paper',
          x: 0.24,
          y: 1.02,
          xanchor: 'center',
          yanchor: 'bottom',
          showarrow: false,
          font: {
            size: 13,
            color: '#1A2940',
            family: 'Arial, sans-serif',
            weight: 600
          }
        },
        // Title for mutation heatmap
        {
          text: 'Unique RSIDS Per Gene Across Disorders',
          xref: 'paper',
          yref: 'paper',
          x: 0.77,
          y: 1.02,
          xanchor: 'center',
          yanchor: 'bottom',
          showarrow: false,
          font: {
            size: 13,
            color: '#1A2940',
            family: 'Arial, sans-serif',
            weight: 600
          }
        },
        ...annotations
      ],
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      hovermode: 'closest',
      showlegend: true,
      legend: {
        x: 1.08,
        y: 1,
        xanchor: 'left',
        yanchor: 'top',
        bgcolor: 'rgba(255,255,255,0.95)',
        bordercolor: '#D1D5DB',
        borderwidth: 1,
        font: {
          size: 9,
          color: '#1A2940',
          family: 'Arial, sans-serif'
        },
        title: {
          text: 'Disorder',
          font: {
            size: 10,
            color: '#1A2940',
            family: 'Arial, sans-serif'
          }
        }
      },
      margin: {
        l: 85,
        r: 180,
        b: 125,
        t: 50,
        pad: 5
      },
      height: uniqueGenes.length * 12 + 200,
      width: 1200,
      autosize: false
    };

    const config = {
      responsive: false,
      displayModeBar: true,
      displaylogo: false,
      scrollZoom: false,
      modeBarButtonsToRemove: ['lasso2d', 'select2d', 'zoom2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'resetScale2d', 'pan2d'],
      toImageButtonOptions: {
        format: 'png',
        filename: 'drug_gene_disorder_matrix',
        height: uniqueGenes.length * 12 + 200,
        width: 1200,
        scale: 2
      }
    };

    Plotly.newPlot(plotRef.current, traces, layout, config);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <MintDBLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h5 className="alert-heading">Error Loading Matrix Data</h5>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="drug-gene-matrix-container">
      {/* Filter Controls */}
      <div className="row mb-3">
        <div className="col-md-4">
          <label className="form-label" style={{ fontWeight: '600', color: '#1A2940', fontSize: '0.9rem' }}>
            Filter by Disorder
          </label>
          <div className="position-relative" ref={disorderDropdownRef}>
            <button
              className="form-control text-start"
              style={{ backgroundColor: 'white', border: '1px solid #ced4da' }}
              onClick={() => setShowDisorderDropdown(!showDisorderDropdown)}
            >
              {selectedDisorders.length === allDisorders.length
                ? 'All Disorders Selected'
                : `${selectedDisorders.length} Disorder(s) Selected`}
            </button>
            {showDisorderDropdown && (
              <div
                className="position-absolute w-100 mt-1 bg-white border rounded shadow"
                style={{ zIndex: 1000, maxHeight: '400px', overflowY: 'auto' }}
              >
                <div className="p-2 border-bottom bg-light">
                  <div className="d-flex gap-2 mb-2">
                    <button
                      className="btn btn-sm btn-outline-primary flex-grow-1"
                      onClick={handleSelectAllDisorders}
                      style={{ fontSize: '0.75rem' }}
                    >
                      Select All
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary flex-grow-1"
                      onClick={handleDeselectAllDisorders}
                      style={{ fontSize: '0.75rem' }}
                    >
                      Deselect All
                    </button>
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search disorders..."
                    value={disorderSearchTerm}
                    onChange={(e) => setDisorderSearchTerm(e.target.value)}
                    style={{ fontSize: '0.85rem' }}
                  />
                </div>
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {allDisorders
                    .filter(disorder =>
                      disorder.toLowerCase().includes(disorderSearchTerm.toLowerCase())
                    )
                    .map(disorder => (
                      <div
                        key={disorder}
                        className="form-check p-2 border-bottom"
                        style={{
                          backgroundColor: selectedDisorders.includes(disorder) ? '#E0F2FE' : 'white',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleDisorderToggle(disorder)}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`disorder-${disorder}`}
                          checked={selectedDisorders.includes(disorder)}
                          onChange={() => handleDisorderToggle(disorder)}
                          style={{ cursor: 'pointer' }}
                        />
                        <label
                          className="form-check-label w-100"
                          htmlFor={`disorder-${disorder}`}
                          style={{
                            cursor: 'pointer',
                            fontWeight: selectedDisorders.includes(disorder) ? '600' : '400'
                          }}
                        >
                          {disorder}
                        </label>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <label className="form-label" style={{ fontWeight: '600', color: '#1A2940', fontSize: '0.9rem' }}>
            Filter by Drug
          </label>
          <div className="position-relative" ref={drugDropdownRef}>
            <button
              className="form-control text-start"
              style={{ backgroundColor: 'white', border: '1px solid #ced4da' }}
              onClick={() => setShowDrugDropdown(!showDrugDropdown)}
            >
              {selectedDrugs.length === availableDrugs.length
                ? `All Drugs Selected (${availableDrugs.length})`
                : `${selectedDrugs.length} / ${availableDrugs.length} Drug(s) Selected`}
            </button>
            {showDrugDropdown && (
              <div
                className="position-absolute w-100 mt-1 bg-white border rounded shadow"
                style={{ zIndex: 1000, maxHeight: '400px', overflowY: 'auto' }}
              >
                <div className="p-2 border-bottom bg-light">
                  <div className="d-flex gap-2 mb-2">
                    <button
                      className="btn btn-sm btn-outline-primary flex-grow-1"
                      onClick={handleSelectAllDrugs}
                      style={{ fontSize: '0.75rem' }}
                    >
                      Select All
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary flex-grow-1"
                      onClick={handleDeselectAllDrugs}
                      style={{ fontSize: '0.75rem' }}
                    >
                      Deselect All
                    </button>
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search drugs..."
                    value={drugSearchTerm}
                    onChange={(e) => setDrugSearchTerm(e.target.value)}
                    style={{ fontSize: '0.85rem' }}
                  />
                </div>
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {availableDrugs
                    .filter(drug =>
                      drug.toLowerCase().includes(drugSearchTerm.toLowerCase())
                    )
                    .map(drug => (
                      <div
                        key={drug}
                        className="form-check p-2 border-bottom"
                        style={{
                          backgroundColor: selectedDrugs.includes(drug) ? '#E0F2FE' : 'white',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleDrugToggle(drug)}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`drug-${drug}`}
                          checked={selectedDrugs.includes(drug)}
                          onChange={() => handleDrugToggle(drug)}
                          style={{ cursor: 'pointer' }}
                        />
                        <label
                          className="form-check-label w-100"
                          htmlFor={`drug-${drug}`}
                          style={{
                            cursor: 'pointer',
                            fontWeight: selectedDrugs.includes(drug) ? '600' : '400'
                          }}
                        >
                          {drug}
                        </label>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <label className="form-label" style={{ fontWeight: '600', color: '#1A2940', fontSize: '0.9rem' }}>
            Filter by Gene
          </label>
          <div className="position-relative" ref={geneDropdownRef}>
            <button
              className="form-control text-start"
              style={{ backgroundColor: 'white', border: '1px solid #ced4da' }}
              onClick={() => setShowGeneDropdown(!showGeneDropdown)}
            >
              {selectedGenes.length === availableGenes.length
                ? `All Genes Selected (${availableGenes.length})`
                : `${selectedGenes.length} / ${availableGenes.length} Gene(s) Selected`}
            </button>
            {showGeneDropdown && (
              <div
                className="position-absolute w-100 mt-1 bg-white border rounded shadow"
                style={{ zIndex: 1000, maxHeight: '400px', overflowY: 'auto' }}
              >
                <div className="p-2 border-bottom bg-light">
                  <div className="d-flex gap-2 mb-2">
                    <button
                      className="btn btn-sm btn-outline-primary flex-grow-1"
                      onClick={handleSelectAllGenes}
                      style={{ fontSize: '0.75rem' }}
                    >
                      Select All
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary flex-grow-1"
                      onClick={handleDeselectAllGenes}
                      style={{ fontSize: '0.75rem' }}
                    >
                      Deselect All
                    </button>
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search genes..."
                    value={geneSearchTerm}
                    onChange={(e) => setGeneSearchTerm(e.target.value)}
                    style={{ fontSize: '0.85rem' }}
                  />
                </div>
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {availableGenes
                    .filter(gene =>
                      gene.toLowerCase().includes(geneSearchTerm.toLowerCase())
                    )
                    .map(gene => (
                      <div
                        key={gene}
                        className="form-check p-2 border-bottom"
                        style={{
                          backgroundColor: selectedGenes.includes(gene) ? '#E0F2FE' : 'white',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleGeneToggle(gene)}
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`gene-${gene}`}
                          checked={selectedGenes.includes(gene)}
                          onChange={() => handleGeneToggle(gene)}
                          style={{ cursor: 'pointer' }}
                        />
                        <label
                          className="form-check-label w-100"
                          htmlFor={`gene-${gene}`}
                          style={{
                            cursor: 'pointer',
                            fontWeight: selectedGenes.includes(gene) ? '600' : '400'
                          }}
                        >
                          {gene}
                        </label>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Combined Visualization */}
      <div style={{
        width: '100%',
        overflowX: 'auto',
        overflowY: 'auto',
        border: '1px solid #D1D5DB',
        backgroundColor: 'white'
      }}>
        <div ref={plotRef} style={{ minWidth: 'fit-content', minHeight: 'fit-content' }} />
      </div>

    </div>
  );
};

export default DrugGeneMatrix;
